
import axios from 'axios';
import moment from 'moment';
import { debounce } from 'lodash';
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { getModule } from 'vuex-module-decorators';
import { CondOperator, SCondition } from '@dataui/crud-request';
import { BIconArrowRepeat, BIconPlus } from 'bootstrap-vue';

import CrudListPagination from '@gid/vue-common/components/crud-list/CrudListPagination.vue';
import CrudListErrorMessage from '@gid/vue-common/components/crud-list/CrudListErrorMessage.vue';
import TicketsModule, {
  TicketJobLinkageEnum,
  isTicketId,
} from '../../store/tickets.module';
import { getPreset, presetToStoreFilter } from './ticketPresets';
import TicketFilterBar from './TicketFilterBar.vue';
import TicketListItem from './TicketListItem.vue';
import TicketDetail from './TicketDetail.vue';
import TicketCreateModal from './TicketCreateModal.vue';

/**
 * Below this the two panes get too short to be worth scrolling, so the console
 * stops shrinking and the page scrolls instead — which is the one case the
 * sticky header actually has to handle.
 */
const MIN_CONSOLE_HEIGHT = 480;

/**
 * Bootstrap's `md`. Below it the panes stack, so the side-by-side full-height
 * layout doesn't apply and the page is left to scroll normally. Kept in sync
 * with the media query in this file's styles.
 */
const PANES_SIDE_BY_SIDE_MIN_WIDTH = 768;

/**
 * How long to give a queued single-ticket sync before re-reading the ticket.
 * Same beat the action bar waits on its own write-through reconcile.
 */
const TICKET_SYNC_SETTLE_MS = 4000;

@Component({
  components: {
    TicketFilterBar,
    TicketListItem,
    TicketDetail,
    TicketCreateModal,
    CrudListPagination,
    CrudListErrorMessage,
    BIconArrowRepeat,
    BIconPlus,
  },
  computed: { ...mapGetters(['locale']) },
})
export default class TicketsConsole extends Vue {
  @Ref('detail') readonly detail!: any;

  locale!: string;

  departments: any[] = [];
  agents: any[] = [];
  selectedFallback: any = null;
  showCreate = false;
  syncing = false;
  lastFetchedAt: number | null = null;
  nowTick = 0;
  consoleHeight: number | null = null;
  private tickTimer: number | null = null;
  private syncTimer: number | null = null;
  private resizeObserver: any = null;

  debouncedRefresh = debounce(() => this.refresh(), 400);

  get store(): TicketsModule {
    return getModule(TicketsModule, this.$store);
  }
  get items(): any[] {
    return this.store.currentPageItems;
  }
  get loading(): boolean {
    return this.store.loading;
  }
  get totalRows(): number | null {
    return this.store.pagination.totalRows ?? null;
  }
  get selectedId(): string | null {
    return (this.$route.query.id as string) || null;
  }
  get activePreset(): string | null {
    return (this.$route.query.view as string) || null;
  }
  get selectedTicket(): any {
    const id = this.selectedId;
    if (!id) return null;
    const inPage = this.items.find((t) => t.id === id);
    if (inPage) return inPage;
    // A deep-linked / off-page ticket is seeded into the store's item map by
    // ensureSelectedLoaded, so the action bar's optimistic patchItem and the
    // reconcile land on the same object the detail pane renders. Prefer it over
    // the raw fetch fallback so edits don't visually revert.
    const stored = this.store.getItem(id);
    if (stored) return stored;
    if (this.selectedFallback && this.selectedFallback.id === id) {
      return this.selectedFallback;
    }
    return null;
  }
  get searchTerm(): string {
    return (this.store.filters.search || '').trim();
  }
  /**
   * The "search all statuses" toggle only relaxes the filter while a term is
   * active — with an empty search box the preset pills stay authoritative.
   */
  get searchIgnoresStatus(): boolean {
    return this.store.filters.searchAllStatuses && !!this.searchTerm;
  }
  /**
   * A ticket id in the search box narrows "Sync now" to that one conversation —
   * the case where the agent knows exactly which ticket is stale and does not
   * want to wait behind an account-wide pull.
   */
  get syncTicketId(): string | null {
    return isTicketId(this.searchTerm) ? this.searchTerm.toUpperCase() : null;
  }
  /** Spell out the scope, since the button does two different things. */
  get syncHint(): string {
    return this.syncTicketId
      ? (this.$t('tickets.sync_now_ticket_hint', {
          id: this.syncTicketId,
        }) as string)
      : (this.$t('tickets.sync_now_hint') as string);
  }
  /** Explains a widened result set, so the pills disagreeing with it isn't a mystery. */
  get searchScopeKey(): string | null {
    if (isTicketId(this.searchTerm)) return 'tickets.search_scope_id';
    if (this.searchIgnoresStatus) return 'tickets.search_scope_all_statuses';
    return null;
  }
  /** Joined rather than templated so the separators don't depend on tag whitespace. */
  get statusLine(): string {
    const parts: string[] = [];
    if (this.totalRows != null) {
      parts.push(this.$t('tickets.results', { n: this.totalRows }) as string);
    }
    if (this.searchScopeKey) {
      parts.push(this.$t(this.searchScopeKey) as string);
    }
    if (this.lastFetchedLabel) parts.push(this.lastFetchedLabel);
    return parts.join(' · ');
  }
  get consoleStyle(): Record<string, string> {
    return this.consoleHeight ? { height: `${this.consoleHeight}px` } : {};
  }
  get lastFetchedLabel(): string {
    if (!this.lastFetchedAt) return '';
    // reference nowTick so the relative label refreshes as time passes
    void this.nowTick;
    return this.$t('tickets.updated_ago', {
      ago: moment(this.lastFetchedAt).locale(this.locale).fromNow(),
    }) as string;
  }

  created() {
    this.store.pagination.perPage = 100;
    this.store.pagination.sortBy = 'createdAt';
    this.store.pagination.sortDir = true; // DESC — newest first
    this.fetchStaticLists();
    const view = this.$route.query.view as string;
    if (view) {
      // applyPreset mutates filters → the deep watcher triggers the fetch
      this.applyPreset(view);
    } else {
      this.refresh();
    }
    this.ensureSelectedLoaded();
  }

  mounted() {
    this.tickTimer = window.setInterval(() => {
      this.nowTick = this.nowTick + 1;
    }, 30000);

    this.measureHeight();
    window.addEventListener('resize', this.measureHeight);
    // A dismissed alert or the AI assistant panel reflows what sits above the
    // console without resizing the window, which moves our top edge.
    const parent = (this.$el as HTMLElement).parentElement;
    if (parent && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measureHeight());
      this.resizeObserver.observe(parent);
    }
  }

  beforeDestroy() {
    if (this.tickTimer) window.clearInterval(this.tickTimer);
    if (this.syncTimer) window.clearTimeout(this.syncTimer);
    window.removeEventListener('resize', this.measureHeight);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /**
   * Size the console so its bottom edge lands on the bottom of the viewport,
   * which is what keeps the page itself from scrolling and therefore what keeps
   * the filter card pinned and each pane's scrollbar independently reachable.
   *
   * Measured rather than hardcoded: the old `calc(100vh - 340px)` was a guess at
   * the height of everything above (back button, alerts, the filter card itself),
   * so it overflowed the page as soon as the filter card grew a row.
   */
  measureHeight() {
    const el = this.$el as HTMLElement;
    if (!el || !el.getBoundingClientRect) return;
    if (window.innerWidth < PANES_SIDE_BY_SIDE_MIN_WIDTH) {
      // Stacked panes — let the console grow and the page scroll as usual.
      this.consoleHeight = null;
      return;
    }
    const top = el.getBoundingClientRect().top + window.scrollY;
    const parent = el.parentElement;
    // The container's bottom padding is the gap we should leave below us.
    const bottomGap = parent
      ? parseFloat(window.getComputedStyle(parent).paddingBottom) || 0
      : 0;
    const next = Math.max(
      MIN_CONSOLE_HEIGHT,
      Math.round(window.innerHeight - top - bottomGap),
    );
    // Our own height feeds back into the observed parent's height — only react to
    // a real change so the ResizeObserver settles instead of looping.
    if (next !== this.consoleHeight) this.consoleHeight = next;
  }

  @Watch('store.filters', { deep: true })
  onFiltersChange() {
    this.debouncedRefresh();
  }

  @Watch('$route.query.view')
  onViewChange(view: string) {
    this.applyPreset(view || null);
  }

  @Watch('selectedId')
  onSelectedIdChange() {
    this.ensureSelectedLoaded();
  }

  buildSearch(): SCondition {
    const and: SCondition[] = [];
    const f = this.store.filters;
    const term = this.searchTerm;

    // A term shaped like a ticket id is a unique lookup, so it drops every other
    // filter — otherwise pasting the id of a closed ticket while the console sits
    // on its default actionable-status preset finds nothing. title/owner stay in
    // the $or because a handful of subjects quote a ticket code verbatim.
    if (isTicketId(term)) {
      return {
        $or: [
          { id: { [CondOperator.EQUALS]: term.toUpperCase() } },
          { title: { [CondOperator.CONTAINS_LOW]: term } },
          { owner: { [CondOperator.CONTAINS_LOW]: term } },
        ],
      };
    }

    if (f.status && f.status.length && !this.searchIgnoresStatus) {
      and.push({ status: { [CondOperator.IN]: f.status } });
    }
    if (f.department) {
      and.push({ departmentId: { [CondOperator.EQUALS]: f.department } });
    }
    if (term) {
      and.push({
        $or: [
          { title: { [CondOperator.CONTAINS_LOW]: term } },
          { owner: { [CondOperator.CONTAINS_LOW]: term } },
          { id: { [CondOperator.CONTAINS_LOW]: term } },
        ],
      });
    }
    if (f.jobLinkage === TicketJobLinkageEnum.HAS) {
      and.push({ 'jobs.id': { [CondOperator.NOT_NULL]: true } });
    } else if (f.jobLinkage === TicketJobLinkageEnum.NONE) {
      and.push({ 'jobs.id': { [CondOperator.IS_NULL]: true } });
    } else if (f.jobLinkage === TicketJobLinkageEnum.SPECIFIC && f.jobName) {
      and.push({ 'jobs.name': { [CondOperator.CONTAINS_LOW]: f.jobName } });
    }
    return and.length ? { $and: and } : {};
  }

  /**
   * Re-read the list the user is currently looking at. Unlike a filter change
   * this keeps the page they're on — jumping back to page 1 would lose their
   * place for what is meant to be a no-op reload.
   */
  refreshList() {
    this.refresh(false);
  }

  refresh(resetPage = true) {
    if (resetPage) this.store.pagination.currentPage = 1;
    this.store.ITEMS_FETCH({
      query: {
        page: this.store.pagination.currentPage,
        limit: this.store.pagination.perPage,
      },
      search: this.buildSearch(),
    });
    this.lastFetchedAt = Date.now();
  }

  applyPreset(view: string | null) {
    const preset = getPreset(view);
    // Mutate the existing filters object in place via SET_TICKET_FILTER — the
    // vuex-module getter has no setter, so reassigning `store.filters` throws.
    // Assigning the pre-declared reactive keys lets the deep watcher pick it up.
    const patch = presetToStoreFilter(preset);
    const filters = this.store.filters;
    filters.status = patch.status;
    filters.jobLinkage = patch.jobLinkage;
    filters.jobName = patch.jobName;
  }

  selectPreset(id: string) {
    if (this.$route.query.view === id) {
      // same pill re-clicked — re-apply so it also clears manual tweaks
      this.applyPreset(id);
      return;
    }
    this.$router
      .push({ query: { ...this.$route.query, view: id } })
      .catch(() => {
        /* ignore duplicate navigation */
      });
  }

  selectTicket(id: string) {
    if (this.$route.query.id === id) return;
    this.$router.push({ query: { ...this.$route.query, id } }).catch(() => {
      /* ignore duplicate navigation */
    });
  }

  async ensureSelectedLoaded() {
    const id = this.selectedId;
    if (!id) {
      this.selectedFallback = null;
      return;
    }
    if (this.items.find((t) => t.id === id)) return;
    try {
      const { data } = await axios.get(`/data-api/ticket/${id}`);
      // Seed into the store map so action-bar edits (patchItem) and the
      // reconcile update the object the detail pane renders, not a stale copy.
      if (data && data.id) this.store.patchItem({ data });
      this.selectedFallback = data;
    } catch (e) {
      this.selectedFallback = null;
    }
  }

  async fetchStaticLists() {
    try {
      const [dep, ag] = await Promise.all([
        axios.get('/data-api/ticket/live-agent/departments'),
        axios.get('/data-api/ticket/live-agent/agents'),
      ]);
      this.departments = dep.data || [];
      this.agents = ag.data || [];
    } catch (e) {
      // Non-fatal: the filter/action controls just show fewer options.
    }
  }

  async syncNow() {
    const ticketId = this.syncTicketId;
    this.syncing = true;
    try {
      await this.store.forceSync(ticketId ? { ticketId } : undefined);
      this.$bvToast.toast(
        ticketId
          ? (this.$t('tickets.sync_started_ticket', { id: ticketId }) as string)
          : (this.$t('tickets.sync_started') as string),
        {
          variant: 'info',
          title: this.$t('tickets.headline') as string,
        },
      );
    } catch (e) {
      this.$bvToast.toast(this.$t('tickets.sync_failed') as string, {
        variant: 'danger',
        title: this.$t('tickets.headline') as string,
      });
      this.syncing = false;
      return;
    }

    // The endpoint only enqueues, so nothing has changed yet when it resolves.
    // A single-ticket sync lands in a bounded moment, so hold the button busy
    // and then re-read that ticket — an account-wide pull has no such moment,
    // so it is left to the list's own refresh.
    if (!ticketId) {
      this.syncing = false;
      return;
    }
    this.syncTimer = window.setTimeout(async () => {
      this.syncTimer = null;
      try {
        await this.afterTicketSync(ticketId);
      } finally {
        this.syncing = false;
      }
    }, TICKET_SYNC_SETTLE_MS);
  }

  /**
   * Pull the freshly synced ticket back in: the list row (it may have just
   * appeared, or changed status) and — if that ticket is the one open in the
   * detail pane — the row plus the conversation, which is what the sync
   * actually went and fetched.
   */
  async afterTicketSync(ticketId: string) {
    this.refresh(false);
    if (this.selectedId !== ticketId || !this.detail) return;
    try {
      await this.detail.refresh();
    } catch (e) {
      /* best-effort — the list refresh above still ran */
    }
  }

  onTicketUpdated() {
    // The row is patched optimistically and reconciled by the action bar;
    // keep it in place so the agent sees the result even if it now falls
    // outside the active status filter.
  }

  onTicketCreated() {
    this.showCreate = false;
    // A newly created ticket syncs into GID a beat later; reload page 1
    // (newest-first) so it surfaces at the top of the list.
    window.setTimeout(() => this.refresh(), 1500);
  }
}
